var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { DeviceDetect } from "sb-resp-utils";
import { computed, observable, observe, action } from "mobx";
import { BlockCondition } from "sb-resp-block-lib";
import { DateRangePeriod, BetTemplateProps, DateTimeType } from "../../../types";
import { config } from "../config";
import { dict } from "../dict";
import { ui } from "../../../contracts";
import { getBetType, sortBetsBySettlementDate, buildDateTimeFormatter, buildCurrencyFormatter, } from "../../../helpers/common";
import { getDateTimeFormatter, getNumberFormatter } from "sb-localization-lib";
const numberOfRecordsToLoad = 10;
const approxBetHeight = 310;
export class BettingHistoryState {
    constructor(options) {
        this.dataObserver = () => { };
        this.timezoneObserver = () => { };
        this.statisticsObserver = () => { };
        this.currencyCodeObserver = () => { };
        this.updateDateRange = (dateRangePeriod) => {
            this.numberOfRecordsToDisplay = this.initialRecordsToDisplay;
            this.parser.updateDateRange(DateRangePeriod[dateRangePeriod]);
            this.setDisplayBets();
            this.fakeLoader = true;
            setTimeout(this.resetLoader, 200);
        };
        this.loadMore = () => {
            this.numberOfRecordsToDisplay += numberOfRecordsToLoad;
            this.parser.loadMore();
        };
        this.numberOfRecordsToDisplay = options.numberOfRecordsToDisplay;
        this.initialRecordsToDisplay = options.numberOfRecordsToDisplay;
        this.dict = options.dict;
        this.deviceDetect = DeviceDetect.getInstance();
        this.parser = options.parser;
        this.betExpandedProps = new Map();
        this.displayBets = observable([]);
        this.fakeLoader = false;
        this.chunkHeights = [0];
        this.chunkSize = numberOfRecordsToLoad;
        this.formatDate = buildDateTimeFormatter(DateTimeType.date, this.config.dateTimeFormat, getDateTimeFormatter);
        this.formatTime = buildDateTimeFormatter(DateTimeType.time, this.config.dateTimeFormat, getDateTimeFormatter);
        this.formatCurrency = buildCurrencyFormatter(this.config.currencyCode, getNumberFormatter);
        this.currencyCodeObserver = observe(this.parser.config, "currencyCode", this.updateCurrencyFormatter);
        this.timezoneObserver = observe(this.parser.config, "currentTimeZone", this.onTimezoneChange);
        this.statisticsProps = {
            totalProfit: this.formatCurrency(0),
            totalStake: this.formatCurrency(0),
            phraseProfit: dict.TotalProfit,
            phraseStake: dict.TotalStake
        };
    }
    activate() {
        this.dataObserver = observe(this.parser.bets, (change) => this.onBetsChange(change));
        this.statisticsObserver = observe(this.parser.statistics, (change) => this.onStatisticsChange(change));
        this.setDisplayBets();
        return this.parser.activate();
    }
    deactivate() {
        this.dataObserver();
        this.timezoneObserver();
        this.statisticsObserver();
        this.currencyCodeObserver();
        this.parser.deactivate();
        this.resetData();
    }
    updateCurrencyFormatter() {
        this.formatCurrency = buildCurrencyFormatter(this.parser.config.currencyCode, getNumberFormatter);
    }
    setExpandedBetDetails(displayBet) {
        var _a;
        if (displayBet.props.selectionDetailsList) {
            displayBet.props.selectionDetailsList.betExpandedProps = {
                expandedBetDetails: !displayBet.props.selectionDetailsList.betExpandedProps.expandedBetDetails
            };
            this.betExpandedProps.set(displayBet.id, {
                expandedSelectionTitles: (_a = displayBet.props.selectionTitlesList) === null || _a === void 0 ? void 0 : _a.betExpandedProps.expandedSelectionTitles,
                expandedBetDetails: displayBet.props.selectionDetailsList.betExpandedProps.expandedBetDetails
            });
        }
    }
    setExpandedSelectionTitles(displayBet) {
        var _a;
        if (displayBet.props.selectionTitlesList) {
            displayBet.props.selectionTitlesList.betExpandedProps = {
                expandedSelectionTitles: !displayBet.props.selectionTitlesList.betExpandedProps.expandedSelectionTitles
            };
            this.betExpandedProps.set(displayBet.id, {
                expandedSelectionTitles: displayBet.props.selectionTitlesList.betExpandedProps.expandedSelectionTitles,
                expandedBetDetails: (_a = displayBet.props.selectionDetailsList) === null || _a === void 0 ? void 0 : _a.betExpandedProps.expandedBetDetails
            });
        }
    }
    get config() {
        return this.parser.config;
    }
    get shouldShowLoadMore() {
        return this.numberOfRecordsToDisplay < this.parser.bets.length;
    }
    get isError() { return this.parser.condition === BlockCondition.ERROR; }
    get isEmpty() { return this.parser.bets.length === 0 || this.parser.condition === BlockCondition.EMPTY; }
    get isLoading() { return this.fakeLoader || this.parser.condition === BlockCondition.LOADING; }
    createDisplayBet(bet) {
        const betType = getBetType(bet);
        const getProps = BetTemplateProps[betType];
        let displayBet = observable({
            id: bet.betId,
            type: betType,
            height: approxBetHeight,
            settlementDate: bet.settlementDate,
            props: getProps(bet, this)
        });
        displayBet = this.extendDisplayBet(displayBet);
        return displayBet;
    }
    extendDisplayBet(displayBet) {
        if (displayBet.props.selectionTitlesList) {
            displayBet.props.selectionTitlesList.setExpandedSelectionTitles = this.setExpandedSelectionTitles.bind(this, displayBet);
        }
        if (displayBet.props.selectionDetailsList) {
            displayBet.props.selectionDetailsList.setExpandedBetDetails = this.setExpandedBetDetails.bind(this, displayBet);
        }
        const chunk = Math.floor((this.displayBets.length) / this.chunkSize);
        displayBet.props.setBetHeight = this.setBetHeight.bind(this, chunk);
        return displayBet;
    }
    get getDisplayBets() { return this.displayBets.slice().sort(sortBetsBySettlementDate).filter((_, i) => i < this.numberOfRecordsToDisplay); }
    setDisplayBets() {
        this.updateCurrencyFormatter();
        this.displayBets.replace(this.parser.bets.map(bet => this.createDisplayBet(bet)));
    }
    changeChunkSize(bet) {
        const chunk = Math.floor((this.displayBets.length) / this.chunkSize);
        if (this.chunkHeights.length <= chunk) {
            this.chunkHeights[chunk] = 0;
        }
        this.chunkHeights[chunk] += bet.height;
    }
    onBetsChange(change) {
        if (change.type === "update") {
            let index = this.displayBets.findIndex(bet => bet.id === change.newValue.id);
            if (index > -1) {
                this.displayBets.splice(index, 1, this.createDisplayBet(change.newValue));
            }
            return;
        }
        if (change.type === "splice") {
            if (change.added.length) {
                const displayBet = this.createDisplayBet(change.added[0]);
                this.changeChunkSize(displayBet);
                this.displayBets.push(displayBet);
            }
            if (change.removed.length) {
                change.removed.forEach(bet => {
                    const betToBeRemoved = this.displayBets.find(displayBet => displayBet.id === bet.betId);
                    if (betToBeRemoved) {
                        this.displayBets.remove(betToBeRemoved);
                    }
                });
            }
        }
    }
    setBetHeight(i, height) {
        this.chunkHeights[i] = Math.round(height);
    }
    getBetExpandedProp(betId, property) {
        let betExpandedProps = this.betExpandedProps.get(betId);
        if (betExpandedProps) {
            return betExpandedProps[property];
        }
        this.betExpandedProps.set(betId, { expandedBetDetails: false, expandedSelectionTitles: false });
        return false;
    }
    onTimezoneChange() {
        this.setDisplayBets();
    }
    onStatisticsChange(change) {
        const changeValue = change.object[change.name];
        this.statisticsProps[change.name] = this.formatCurrency(changeValue);
    }
    resetData() {
        this.displayBets.length = 0;
        this.chunkHeights.length = 0;
        this.betExpandedProps.clear();
        this.statisticsProps.totalProfit = this.formatCurrency(0);
        this.statisticsProps.totalStake = this.formatCurrency(0);
    }
    resetLoader() {
        this.fakeLoader = false;
    }
}
__decorate([
    observable,
    __metadata("design:type", Number)
], BettingHistoryState.prototype, "numberOfRecordsToDisplay", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], BettingHistoryState.prototype, "displayBets", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], BettingHistoryState.prototype, "statisticsProps", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], BettingHistoryState.prototype, "fakeLoader", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], BettingHistoryState.prototype, "chunkHeights", void 0);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], BettingHistoryState.prototype, "updateCurrencyFormatter", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], BettingHistoryState.prototype, "setExpandedBetDetails", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], BettingHistoryState.prototype, "setExpandedSelectionTitles", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], BettingHistoryState.prototype, "config", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], BettingHistoryState.prototype, "shouldShowLoadMore", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], BettingHistoryState.prototype, "isError", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], BettingHistoryState.prototype, "isEmpty", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], BettingHistoryState.prototype, "isLoading", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], BettingHistoryState.prototype, "getDisplayBets", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], BettingHistoryState.prototype, "setDisplayBets", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], BettingHistoryState.prototype, "onBetsChange", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, Number]),
    __metadata("design:returntype", void 0)
], BettingHistoryState.prototype, "setBetHeight", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], BettingHistoryState.prototype, "onTimezoneChange", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], BettingHistoryState.prototype, "onStatisticsChange", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], BettingHistoryState.prototype, "resetData", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], BettingHistoryState.prototype, "resetLoader", null);
//# sourceMappingURL=BettingHistoryState.js.map